<template>
    <div class="wizard-forms">
        <div class="row">
            <div class="col-md-10 col-md-offset-1">
                <div>
                    <h2>{{ $t('dynamicess.wizard.buyPrices') }}</h2>
                    <p>
                        {{ $t('dynamicess.buyPricesDescription') }}
                    </p>
                </div>
                <div class="vrm-form__fieldset-item vrm-form__fieldset-item--checkbox">
                    <label class="sg-textfield-label">
                        <span class="sg-textfield-label-caption">
                            {{ $t('dynamicess.buyPricesQuestionFixed') }}
                        </span>
                    </label>
                    <div
                        class="vrm-form__custom-radio-checkbox vrm-form__custom-radio-checkbox--inline"
                    >
                        <div
                            v-for="optionType in Object.values(BUY_PRICES)"
                            :key="optionType"
                            class="sg-custom-check-and-radio custom-radio "
                            :class="{ active: fixedBuyPrices === optionType }"
                            @click="
                                fixedBuyPrices = optionType;
                                errors.fixedBuyPrices = false;
                            "
                        >
                            <label>
                                <input type="radio" :checked="fixedBuyPrices === optionType" />
                                <div class="sg-alternative-display">
                                    <div class="sg-outer">
                                        <div class="sg-inner"></div>
                                    </div>
                                </div>
                            </label>
                            <label for="buyDifferentPricesWeek">
                                {{ $t('dynamicess.buy_type_' + optionType) }}
                            </label>
                            <InputError v-show="errors.fixedBuyPrices"></InputError>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <generic-prices
            type="buy"
            :different-prices-week="buyDifferentPricesWeek"
            :rows-week="buyRowsWeek"
            :rows-weekend="buyRowsWeekend"
            :price-formula="buyPriceFormula"
            :fixed-prices="fixedBuyPrices"
            :provider="buyProvider"
            :provider-name="providerName"
            toggle-neg-price-clamping-property="buyPriceClampNegativeToZero"
            :account-number="accountNumber"
            :tempo-overwrite-blue-day-hours="tempoOverwriteBlueDayHours"
            :tempo-overwrite-blue-night-hours="tempoOverwriteBlueNightHours"
            :tempo-overwrite-white-day-hours="tempoOverwriteWhiteDayHours"
            :tempo-overwrite-white-night-hours="tempoOverwriteWhiteNightHours"
            :tempo-overwrite-red-day-hours="tempoOverwriteRedDayHours"
            :tempo-overwrite-red-night-hours="tempoOverwriteRedNightHours"
            :api-key="apiKey"
            :direct-debit="directDebit"
            :country="country"
            :bidding-zone="biddingZone"
            :price-sampling-rate="buyPriceSamplingRate"
            @update:price-formula="buyPriceFormula = $event"
            @update:different-prices-week="buyDifferentPricesWeek = $event"
            @update:provider="buyProvider = $event"
            @update:provider-name="providerName = $event"
            @update:api-key="apiKey = $event"
            @update:vat="onChangeVat"
            @update:account-number="accountNumber = $event"
            @update:direct-debit="directDebit = $event"
            @update:country="country = $event"
            @update:price-sampling-rate="buyPriceSamplingRate = $event"
            @rows-updated="setFixedPrices"
            @error="setError"
            @update:bidding-zone="biddingZone = $event"
            @update:tempo-overwrite-blue-day-hours="tempoOverwriteBlueDayHours = $event"
            @update:tempo-overwrite-blue-night-hours="tempoOverwriteBlueNightHours = $event"
            @update:tempo-overwrite-white-day-hours="tempoOverwriteWhiteDayHours = $event"
            @update:tempo-overwrite-white-night-hours="tempoOverwriteWhiteNightHours = $event"
            @update:tempo-overwrite-red-day-hours="tempoOverwriteRedDayHours = $event"
            @update:tempo-overwrite-red-night-hours="tempoOverwriteRedNightHours = $event"
        />
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import SelectorPrices from '@/vue/components/pages/dynamic-ess/forms/SelectorPrices.vue';
import InputError from '@/vue/components/forms/InputError.vue';
import GenericPrices from '@/vue/components/pages/dynamic-ess/forms/GenericPrices.vue';
import { DYNAMIC_ESS, DYNAMIC_ESS_FORM_FIELDS } from '@/vue/store/modules/dynamic-ess-form.js';

@Component({
    components: {
        SelectorPrices,
        InputError,
        GenericPrices,
    },
})
export default class BuyForm extends Vue {
    mounted() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    YES_NO = {
        YES: 1,
        NO: 0,
    };
    BUY_PRICES = {
        FIXED: 1,
        DYNAMIC: 0,
    };
    errors = {
        buyDifferentPricesWeek: null,
        fixedBuyPrices: null,
    };

    @Prop()
    initialValues;
    changedField = new Map();

    created() {
        let fields = [
            'buyPriceFormula',
            'fixedBuyPrices',
            'buyDifferentPricesWeek',
            'buyRowsWeek',
            'buyRowsWeekend',
            'buyProvider',
            'buyProviderName',
            'accountNumber',
            'directDebit',
            'vat',
            'apiKey',
            'country',
            'buyPriceClampNegativeToZero',
            'tempoOverwriteBlueDayHours',
            'tempoOverwriteBlueNightHours',
            'tempoOverwriteWhiteDayHours',
            'tempoOverwriteWhiteNightHours',
            'tempoOverwriteRedDayHours',
            'tempoOverwriteRedNightHours',
        ];
        let initialValuesAllNull = true;
        fields.forEach(field => {
            this.changedField.set(field, false);
            if (this.initialValues[field] !== null) {
                initialValuesAllNull = false;
            }
        });
        this.creating = initialValuesAllNull;
    }

    compareAndEmitEdit(field, newValue) {
        if (this.initialValues[field] === null) {
            this.changedField.set(field, true);
        } else {
            if (field === 'buyRowsWeek' || field === 'buyRowsWeekend') {
                let hasChange = false;
                if (newValue.length !== this.initialValues[field].length) {
                    hasChange = true;
                } else {
                    for (let i = 0; i < this.initialValues[field].length; i++) {
                        hasChange =
                            hasChange ||
                            newValue[i].from.time !== this.initialValues[field][i].from.time ||
                            newValue[i].to.time !== this.initialValues[field][i].to.time ||
                            newValue[i].price.value.toString() !==
                                this.initialValues[field][i].price.value.toString();
                    }
                }
                this.changedField.set(field, hasChange);
            } else if (field === 'buyProvider') {
                this.changedField.set(
                    field,
                    newValue.name !== this.initialValues['buyProvider'].name,
                );
            } else {
                this.changedField.set(
                    field,
                    newValue.toString() !== this.initialValues[field].toString(),
                );
            }
        }
        let hasChange = false;
        this.changedField.forEach(value => {
            hasChange = hasChange || value;
        });
        if (this.creating) {
            this.$emit('creating', true);
        }
        this.$emit('change', {
            hasChange: hasChange,
            formName: 'buyForm',
        });
    }

    get buyPriceFormula() {
        return this.$store.state.dynamicEssForm.buyPriceFormula;
    }

    set buyPriceFormula(value) {
        this.compareAndEmitEdit('buyPriceFormula', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.buyPriceFormula,
        });
    }

    get fixedBuyPrices() {
        return this.$store.state.dynamicEssForm.fixedBuyPrices;
    }

    set fixedBuyPrices(value) {
        this.compareAndEmitEdit('fixedBuyPrices', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.fixedBuyPrices,
        });
    }

    get buyDifferentPricesWeek() {
        return this.$store.state.dynamicEssForm.buyDifferentPricesWeek;
    }

    set buyDifferentPricesWeek(value) {
        this.compareAndEmitEdit('buyDifferentPricesWeek', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.buyDifferentPricesWeek,
        });
    }

    get buyRowsWeek() {
        return this.$store.state.dynamicEssForm.buyRowsWeek;
    }

    get buyRowsWeekend() {
        return this.$store.state.dynamicEssForm.buyRowsWeekend;
    }

    get buyProvider() {
        return this.$store.state.dynamicEssForm.buyProvider;
    }

    set buyProvider(value) {
        this.compareAndEmitEdit('buyProvider', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.buyProvider,
        });
    }

    get providerName() {
        return this.$store.state.dynamicEssForm.buyProviderName;
    }

    set providerName(value) {
        this.compareAndEmitEdit('buyProviderName', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.buyProviderName,
        });
    }

    get buyPriceSamplingRate() {
        return this.$store.state.dynamicEssForm.buyPriceSamplingRate;
    }

    set buyPriceSamplingRate(value) {
        this.compareAndEmitEdit('buyPriceSamplingRate', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.buyPriceSamplingRate,
        });
    }

    get buyPriceClampNegativeToZero() {
        return this.$store.state.dynamicEssForm.buyPriceClampNegativeToZero;
    }

    @Watch('buyPriceClampNegativeToZero')
    onBuyPriceClampNegativeToZero(value) {
        this.compareAndEmitEdit('buyPriceClampNegativeToZero', value);
    }

    get directDebit() {
        return this.$store.state.dynamicEssForm.directDebit;
    }

    set directDebit(value) {
        this.compareAndEmitEdit('directDebit', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.directDebit,
        });
    }

    get apiKey() {
        return this.$store.state.dynamicEssForm.apiKey;
    }

    set apiKey(value) {
        this.compareAndEmitEdit('apiKey', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.apiKey,
        });
    }

    get accountNumber() {
        return this.$store.state.dynamicEssForm.accountNumber;
    }

    set accountNumber(value) {
        this.compareAndEmitEdit('accountNumber', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.accountNumber,
        });
    }

    get country() {
        return this.$store.state.dynamicEssForm.country;
    }

    set country(value) {
        this.compareAndEmitEdit('country', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.country,
        });
    }

    onChangeVat(value) {
        this.compareAndEmitEdit('vat', value);
    }

    setError(error) {
        if (error.week) {
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: true,
                key: DYNAMIC_ESS_FORM_FIELDS.buyErrorRowsWeek,
            });
        } else if (error.weekend) {
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: true,
                key: DYNAMIC_ESS_FORM_FIELDS.buyErrorRowsWeekend,
            });
        } else if (typeof error.formula === 'boolean') {
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: error.formula,
                key: DYNAMIC_ESS_FORM_FIELDS.buyErrorPriceFormula,
            });
        }
    }

    setFixedPrices(rows) {
        if (rows.week) {
            this.compareAndEmitEdit('buyRowsWeek', rows.week);
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: rows.week,
                key: DYNAMIC_ESS_FORM_FIELDS.buyRowsWeek,
            });
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: false,
                key: DYNAMIC_ESS_FORM_FIELDS.buyErrorRowsWeek,
            });
        } else if (rows.weekend) {
            this.compareAndEmitEdit('buyRowsWeekend', rows.weekend);
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: rows.weekend,
                key: DYNAMIC_ESS_FORM_FIELDS.buyRowsWeekend,
            });
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: false,
                key: DYNAMIC_ESS_FORM_FIELDS.buyErrorRowsWeekend,
            });
        }
    }

    get biddingZone() {
        return this.$store.state.dynamicEssForm.biddingZone;
    }

    set biddingZone(value) {
        this.compareAndEmitEdit('biddingZone', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.biddingZone,
        });
    }

    get tempoOverwriteBlueDayHours() {
        return this.$store.state.dynamicEssForm.tempoOverwriteBlueDayHours;
    }

    set tempoOverwriteBlueDayHours(value) {
        this.compareAndEmitEdit('tempoOverwriteBlueDayHours', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.tempoOverwriteBlueDayHours,
        });
    }

    get tempoOverwriteBlueNightHours() {
        return this.$store.state.dynamicEssForm.tempoOverwriteBlueNightHours;
    }

    set tempoOverwriteBlueNightHours(value) {
        this.compareAndEmitEdit('tempoOverwriteBlueNightHours', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.tempoOverwriteBlueNightHours,
        });
    }

    get tempoOverwriteWhiteDayHours() {
        return this.$store.state.dynamicEssForm.tempoOverwriteWhiteDayHours;
    }

    set tempoOverwriteWhiteDayHours(value) {
        this.compareAndEmitEdit('tempoOverwriteWhiteDayHours', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.tempoOverwriteWhiteDayHours,
        });
    }

    get tempoOverwriteWhiteNightHours() {
        return this.$store.state.dynamicEssForm.tempoOverwriteWhiteNightHours;
    }

    set tempoOverwriteWhiteNightHours(value) {
        this.compareAndEmitEdit('tempoOverwriteWhiteNightHours', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.tempoOverwriteWhiteNightHours,
        });
    }

    get tempoOverwriteRedDayHours() {
        return this.$store.state.dynamicEssForm.tempoOverwriteRedDayHours;
    }

    set tempoOverwriteRedDayHours(value) {
        this.compareAndEmitEdit('tempoOverwriteRedDayHours', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.tempoOverwriteRedDayHours,
        });
    }

    get tempoOverwriteRedNightHours() {
        return this.$store.state.dynamicEssForm.tempoOverwriteRedNightHours;
    }

    set tempoOverwriteRedNightHours(value) {
        this.compareAndEmitEdit('tempoOverwriteRedNightHours', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.tempoOverwriteRedNightHours,
        });
    }
}
</script>
